import { ChevronUp, ChevronDown, Trash2, Plus, Lock } from 'lucide-react';
import { showAlert } from '@/components/AppDialog';
import {
  ICON_NAMES, PLAN_ICONS, PROTECTED_PLAN_IDS, NEW_PLAN_CONTENT,
  type PlanContent,
} from '@/data/setupPricing';

/* Editor for one flow's ordered plan cards (setupPricing/current.setupPlans or
   .operatePlans). Presentational — the parent owns state, loading and saving.

   `id` is deliberately read-only. It is the funnel's key (SetupPlan routes `starter`
   to the expedited step, SetupUpgrade sells `compliance`) AND it is persisted onto
   every client order as `planKey`, so renaming one would both break the flow and
   orphan historical orders. Titles are what customers see; edit those freely. */

const txtCls = 'w-full border-2 border-gray-200 rounded-lg px-3 py-2 text-sm font-semibold bg-amber-50 text-gray-900 focus:outline-none focus:border-amber-400';
const areaCls = 'w-full border-2 border-gray-200 rounded-lg px-3 py-2 text-sm bg-amber-50 text-gray-900 focus:outline-none focus:border-amber-400 leading-relaxed';
const lbl = 'text-[11px] font-bold text-gray-400 uppercase tracking-wide block mb-1';

const slugify = (s: string) =>
  s.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'plan';

/** A new id that collides with neither this flow nor the other one. */
function uniqueId(base: string, taken: string[]): string {
  if (!taken.includes(base)) return base;
  let n = 2;
  while (taken.includes(`${base}-${n}`)) n++;
  return `${base}-${n}`;
}

export default function SetupPlanEditor({
  title,
  hint,
  plans,
  onChange,
  reservedIds = [],
}: {
  title: string;
  hint: string;
  plans: PlanContent[];
  onChange: (next: PlanContent[]) => void;
  /** Ids used by the other flow — avoided when generating a new id. */
  reservedIds?: string[];
}) {
  const patch = (i: number, fn: (p: PlanContent) => void) => {
    const next = plans.map(p => ({ ...p, features: [...p.features] }));
    const target = next[i];
    if (!target) return;
    fn(target);
    onChange(next);
  };

  const move = (i: number, dir: -1 | 1) => {
    const j = i + dir;
    if (j < 0 || j >= plans.length) return;
    const next = [...plans];
    const a = next[i];
    const b = next[j];
    if (!a || !b) return;
    next[i] = b;
    next[j] = a;
    onChange(next);
  };

  const add = () => {
    const id = uniqueId('new-plan', [...plans.map(p => p.id), ...reservedIds]);
    onChange([...plans, NEW_PLAN_CONTENT(id)]);
  };

  const remove = (i: number) => {
    const p = plans[i];
    if (!p) return;
    if (PROTECTED_PLAN_IDS.includes(p.id)) {
      showAlert(`“${p.title}” can't be removed. The signup flow depends on its id (${p.id}). You can still rename it, rewrite its features, or move it down the order.`);
      return;
    }
    onChange(plans.filter((_, j) => j !== i));
  };

  return (
    <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
      <div className="h-1.5 bg-gradient-to-r from-amber-400 to-amber-500" />
      <div className="p-5">
        <div className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-1">{title}</div>
        <p className="text-[11px] text-gray-400 font-medium mb-4">{hint}</p>

        <div className="space-y-4">
          {plans.map((p, i) => {
            const Icon = PLAN_ICONS[p.icon] ?? PLAN_ICONS.tag;
            const locked = PROTECTED_PLAN_IDS.includes(p.id);
            return (
              <div key={p.id} className="border-2 border-gray-100 rounded-xl p-4">
                {/* Card head: order controls, identity, delete */}
                <div className="flex items-center gap-2 mb-3">
                  <div className="flex flex-col">
                    <button onClick={() => move(i, -1)} disabled={i === 0} title="Move up"
                      className="text-gray-300 hover:text-gray-600 disabled:opacity-30 disabled:hover:text-gray-300"><ChevronUp className="w-4 h-4" /></button>
                    <button onClick={() => move(i, 1)} disabled={i === plans.length - 1} title="Move down"
                      className="text-gray-300 hover:text-gray-600 disabled:opacity-30 disabled:hover:text-gray-300"><ChevronDown className="w-4 h-4" /></button>
                  </div>
                  <Icon className="w-5 h-5 text-gray-500 shrink-0" strokeWidth={1.5} />
                  <span className="text-sm font-bold text-gray-800 truncate">{p.title || '(untitled)'}</span>
                  <span className="text-[10px] font-mono font-bold text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded shrink-0" title="Internal id, referenced by the signup flow and stored on past orders, so it can't be changed.">
                    {p.id}
                  </span>
                  <span className="text-[10px] font-bold text-gray-300 shrink-0">#{i + 1}</span>
                  <div className="flex-1" />
                  {locked
                    ? <span className="text-gray-300 p-1" title={`Required by the signup flow, can't be removed`}><Lock className="w-4 h-4" /></span>
                    : <button onClick={() => remove(i)} className="text-gray-300 hover:text-red-500 transition-colors p-1" title="Remove plan"><Trash2 className="w-4 h-4" /></button>}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div><label className={lbl}>Title</label>
                    <input value={p.title} onChange={e => patch(i, d => { d.title = e.target.value; })} className={txtCls} /></div>
                  <div><label className={lbl}>Title note (optional)</label>
                    <input value={p.titleNote} onChange={e => patch(i, d => { d.titleNote = e.target.value; })} placeholder="(For New Business Setup)" className={txtCls} /></div>
                </div>

                <div className="mt-3"><label className={lbl}>Description</label>
                  <textarea value={p.description} onChange={e => patch(i, d => { d.description = e.target.value; })} rows={2} className={areaCls} /></div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-3">
                  <div><label className={lbl}>Badge (amber line)</label>
                    <input value={p.badge ?? ''} onChange={e => patch(i, d => { d.badge = e.target.value || null; })} placeholder="e.g. 30-Day Free Trial" className={txtCls} /></div>
                  <div><label className={lbl}>Discount pill</label>
                    <input value={p.discountLabel ?? ''} onChange={e => patch(i, d => { d.discountLabel = e.target.value || null; })} placeholder="e.g. 20% OFF" className={txtCls} /></div>
                  <div><label className={lbl}>Price note</label>
                    <input value={p.priceNote} onChange={e => patch(i, d => { d.priceNote = e.target.value; })} placeholder="(+ government fees)" className={txtCls} /></div>
                </div>

                <div className="mt-3"><label className={lbl}>Features heading</label>
                  <input value={p.featuresLabel} onChange={e => patch(i, d => { d.featuresLabel = e.target.value; })} placeholder="e.g. Everything in Starter +" className={txtCls} /></div>

                <div className="mt-3"><label className={lbl}>Features (one per line)</label>
                  <textarea value={p.features.join('\n')} onChange={e => patch(i, d => { d.features = e.target.value.split('\n').map(s => s.trimEnd()).filter(Boolean); })} rows={Math.max(3, p.features.length)} className={areaCls} /></div>

                <div className="flex flex-wrap items-center gap-5 mt-3">
                  <div className="flex items-center gap-2">
                    <label className="text-[11px] font-bold text-gray-400 uppercase tracking-wide">Icon</label>
                    <select value={p.icon} onChange={e => patch(i, d => { d.icon = e.target.value as PlanContent['icon']; })}
                      className="border-2 border-gray-200 rounded-lg px-2 py-1.5 text-sm font-semibold bg-amber-50 text-gray-900 focus:outline-none focus:border-amber-400">
                      {ICON_NAMES.map(n => <option key={n} value={n}>{n}</option>)}
                    </select>
                  </div>
                  <label className="flex items-center gap-1.5 text-xs font-bold text-gray-600">
                    <input type="checkbox" checked={p.mostPopular} onChange={e => patch(i, d => { d.mostPopular = e.target.checked; })} /> Most popular
                  </label>
                </div>
              </div>
            );
          })}
        </div>

        <button onClick={add} className="flex items-center gap-1.5 text-amber-700 hover:text-amber-800 font-bold text-xs mt-3">
          <Plus className="w-4 h-4" /> Add plan
        </button>
      </div>
    </div>
  );
}
